/**
 * ConstraintParetoCard — ranked binding-constraints table + Pareto-buildup
 * waterfall for a single DUID.
 *
 * Lived inline on PortfolioPerformance until the 2026-06-10 METIS review,
 * where the team moved the binding-constraints table to the bottom of the
 * Curtailment tab ("the one below there with all the different curtailment
 * binding constraints? That should go into curtailment"). Extracted to its
 * own file so the Curtailment page can render it without importing the
 * whole Performance page.
 *
 * Real data only, from atlas.constraint_binding (DISPATCHCONSTRAINT ×
 * GENCONDATA), served via /technical. While the request is in flight the
 * card shows a skeleton; when the DUID has no AEMO unit-tagged constraints
 * in the window it shows an explicit empty state (network-wide constraints
 * aren't attributed here yet — that's a future SPDCONNECTIONPOINTCONSTRAINT
 * join). It NEVER fabricates data — the old synthetic seeded fallback made a
 * smaller window (no real constraints → random hours) out-total a larger
 * window (real, smaller hours), which read as "30d > 90d". Removed.
 */

import React, { useState, useMemo } from 'react';
import { Box, Card, Stack, Typography, Skeleton } from '@mui/material';
import WaterfallChart from './WaterfallChart';

// Human-readable names for well-known constraint codes — a fallback for the
// rare real row that arrives without a `description` from the server. Kept as
// the single read-site so the lookup is one-line.
const CONSTRAINT_NAMES = {
  'N>>NIL_BL1':         'NSW boundary loop · voltage stability',
  'Q>NIL_BI1':          'QNI northern flow · interconnect limit',
  'S>NIL_NSW1':         'SA ↔ NSW · Heywood interconnect',
  'V>>S_NIL':           'VIC ↔ Snowy · southern flow',
  'F_MAIN+NIL_WF':      'Mainland frequency · wind farm trip',
  'NIL_FCAS_RAISE':     'Mainland FCAS raise reserve',
  'NIL_FCAS_RAISE_REG': 'Mainland regulation raise',
  'NIL_FCAS_LOWER_REG': 'Mainland regulation lower',
  'N::N_NIL_BL':        'NSW thermal · boundary limit',
  'Q::Q_NIL_BI':        'QLD thermal · interconnect support',
  'S::S_NIL_SE':        'SA thermal · south-east region',
};

export const friendlyConstraintName = (code) => CONSTRAINT_NAMES[code] || code;

export const fmtHrs = (h) => {
  if (h == null || isNaN(h)) return '—';
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
};

export default function ConstraintParetoCard({
  constraintBinding, loading, colors,
}) {
  // Row-hover state used to highlight the matching bar in the waterfall.
  // We store the step `label` (e.g. "#1") rather than an index so the
  // mapping survives when the rows array reorders.
  const [hoveredLabel, setHoveredLabel] = useState(null);
  const rows = useMemo(() => {
    const real = constraintBinding?.pareto;
    if (!(real && real.length)) return [];
    // share_pct + cum_pct are server-computed (see
    // modules/portfolio/technical.py:_enrich_constraint_binding) so the AI
    // agent and the UI see identical numbers. Read the fields as-is. No
    // synthetic fallback: an empty window renders the empty state below, so a
    // smaller window can never out-total a larger one.
    return real.map((r) => ({
      code: r.code, description: r.description, reason: r.reason,
      hrs: r.hours || 0, shadow: r.max_marginal_value,
      sharePct: r.share_pct ?? 0,
      cumPct:   r.cum_pct ?? 0,
    }));
  }, [constraintBinding]);

  // How many constraints make up the "vital few" (cumulative ≤ 80%).
  // Surface this in the header chip so users see the headline up front
  // ("3 of 12 constraints = 80% of bound hours").
  const vitalFewCount = rows.filter((r) => r.cumPct <= 80).length;
  const totalHrs = rows.reduce((a, b) => a + b.hrs, 0);

  // Pareto-buildup waterfall: each constraint is a 'gain' step adding to a
  // running total; the final 'end' pillar shows total bound hours. The
  // top-6 + "Other" bundling is now server-computed in
  // modules/portfolio/technical.py:_enrich_constraint_binding (so the AI
  // agent gets the same chart shape without re-deriving). We just decorate
  // with colours + add the closing "Total" pillar.
  const waterfallSteps = useMemo(() => {
    if (!rows.length) return [];
    const serverWaterfall = constraintBinding?.waterfall;
    const base = (serverWaterfall && serverWaterfall.length)
      ? serverWaterfall.map((s, i) => ({
          label: s.code === 'OTHER' ? 'Other' : `#${i + 1}`,
          kind:  'gain',
          value: s.hours,
          // Amber for vital-few (cum ≤ 80%); slate for the rest. We look
          // up cumPct from `rows` for the same code, with a sensible
          // default for the "Other" bucket.
          color: s.code === 'OTHER'
            ? '#64748b'
            : (rows.find((r) => r.code === s.code)?.cumPct ?? 100) <= 80
              ? '#f59e0b' : '#64748b',
        }))
      : [];
    return base.concat([{
      label: 'Total', kind: 'end', value: totalHrs, color: '#22c55e',
    }]);
  }, [rows, totalHrs, constraintBinding]);

  return (
    <Card sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          Constraint Pareto
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
          {rows.length} binding
          {vitalFewCount > 0 && rows.length > 0 && (
            <>
              {' '}·{' '}
              <Box component="span" sx={{ color: '#f59e0b', fontWeight: 600 }}>
                top {vitalFewCount} = 80%
              </Box>
            </>
          )}
        </Typography>
      </Stack>
      {loading ? (
        <Skeleton variant="rectangular" height={260} sx={{ bgcolor: colors.skeleton, borderRadius: 1 }} />
      ) : !rows.length ? (
        <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.disabled">No DUID-tagged constraints bound this asset in this period.</Typography>
        </Box>
      ) : (
        <Box sx={{
          // Two-column layout: table on the left (40%), Pareto-buildup
          // waterfall on the right (60%). The table is the read-the-numbers
          // surface; the waterfall is the visual story.
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '4fr 6fr' },
          gap: 2,
          alignItems: 'start',
        }}>
          {/* ── LEFT: ranked table ─────────────────────────────────────── */}
          <Box>
            {/* Header row */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '0.4fr 2.6fr 0.9fr 0.7fr',
              columnGap: 1,
              px: 1, pb: 0.5,
              borderBottom: '1px solid', borderColor: 'divider',
            }}>
              {['#', 'Constraint', 'Hours', '% share'].map((h, i) => (
                <Typography key={h} variant="caption" sx={{
                  color: 'text.disabled', letterSpacing: '0.08em', textTransform: 'uppercase',
                  fontSize: '0.65rem',
                  textAlign: i >= 2 ? 'right' : 'left',
                }}>{h}</Typography>
              ))}
            </Box>
            {rows.map((r, i) => {
              const rank = i + 1;
              const isVital = r.cumPct <= 80;
              // Bars #1–#6 map directly to ranks; ranks beyond that fall
              // into the bundled "Other" bar on the waterfall.
              // The server's waterfall is the source of truth for top-N vs
              // "Other" bundling — look up the row's matching bar label.
              const wf = constraintBinding?.waterfall || [];
              const wfEntry = wf.find((s) => s.code === r.code);
              const barLabel = wfEntry
                ? (wfEntry.code === 'OTHER' ? 'Other' : `#${rank}`)
                : (rank <= 6 ? `#${rank}` : 'Other');
              return (
                <Box
                  key={r.code}
                  onMouseEnter={() => setHoveredLabel(barLabel)}
                  onMouseLeave={() => setHoveredLabel(null)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '0.4fr 2.6fr 0.9fr 0.7fr',
                    columnGap: 1,
                    alignItems: 'center',
                    px: 1, py: 0.65,
                    borderRadius: 1,
                    cursor: 'pointer',
                    // Sticky tint while hovered so the row–bar pairing
                    // reads even after the user's eye moves to the chart.
                    bgcolor: hoveredLabel === barLabel
                      ? 'rgba(245, 158, 11, 0.08)'
                      : 'transparent',
                    '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.08)' },
                    transition: 'background-color 100ms ease',
                  }}>
                  {/* Rank — matches the waterfall x-axis label so the eye
                      links table row → bar without effort. Vital-few rows
                      stay amber to reinforce the "focus here" signal. */}
                  <Typography sx={{
                    fontSize: '0.72rem', fontFamily: 'monospace',
                    color: isVital ? '#f59e0b' : 'text.disabled',
                    fontWeight: 600,
                  }}>
                    #{rank}
                  </Typography>
                  {/* Description as primary, raw code + reason as subtitle */}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      title={r.description ? `${r.code} — ${r.description}` : r.code}
                      sx={{
                        fontSize: '0.8rem', fontWeight: 500, color: 'text.primary',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >
                      {r.description || friendlyConstraintName(r.code)}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.65rem', fontFamily: 'monospace',
                      color: 'text.disabled', mt: 0.1,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {r.code}{r.reason ? `  ·  ${r.reason}` : ''}
                    </Typography>
                  </Box>
                  <Typography sx={{
                    fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums',
                    color: 'text.primary', textAlign: 'right',
                  }}>
                    {fmtHrs(r.hrs)}
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums',
                    color: isVital ? '#f59e0b' : 'text.disabled',
                    textAlign: 'right',
                  }}>
                    {/* Real-but-tiny shares shouldn't read as zero. */}
                    {r.sharePct > 0 && r.sharePct < 0.05
                      ? '<0.1%'
                      : `${r.sharePct.toFixed(1)}%`}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* ── RIGHT: Pareto-buildup waterfall ──────────────────────────
              Heightened to 340 so the cumulative steps don't look squished.
              `highlightLabel` is the controlled hover bridge from the table
              — when a row is hovered, the matching bar pops + the rest fade.
              `seriesLabels` renames the generic "Gain"/"End" series labels
              to the constraint-specific "Constraint"/"Total". */}
          <Box>
            <WaterfallChart
              steps={waterfallSteps}
              formatValue={fmtHrs}
              height={340}
              colors={colors}
              highlightLabel={hoveredLabel}
              seriesLabels={{ gain: 'Constraint', end: 'Total' }}
            />
          </Box>
        </Box>
      )}
    </Card>
  );
}
